import { ArrowRight, CheckCircle2, Eye, EyeOff, KeyRound, LockKeyhole, Mail, RefreshCw, ShieldCheck, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { assertOnline, withTimeout } from '../lib/async';
import { friendlyAuthError } from '../lib/errors';
import { reportClientError } from '../lib/monitoring';
import { supabase } from '../lib/supabase';

type AuthMode = 'signin' | 'signup' | 'forgot' | 'recovery';
type AuthAction = AuthMode | 'google' | 'resend';

interface AuthScreenProps {
  configured: boolean;
  recoveryMode: boolean;
  initialMode?: 'signin' | 'signup';
  onBack?: () => void;
  onRecoveryComplete: () => void;
}

const actionLabels: Record<AuthAction, string> = {
  signin: 'กำลังเข้าสู่ระบบ…', signup: 'กำลังสร้างบัญชี…', forgot: 'กำลังส่งอีเมล…',
  recovery: 'กำลังบันทึกรหัสผ่าน…', google: 'กำลังเชื่อมต่อ Google…', resend: 'กำลังส่งอีเมลอีกครั้ง…',
};

export function AuthScreen({ configured, recoveryMode, initialMode = 'signin', onBack, onRecoveryComplete }: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>(recoveryMode ? 'recovery' : initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [busyAction, setBusyAction] = useState<AuthAction | null>(null);
  const [lastFailedAction, setLastFailedAction] = useState<AuthAction | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  const busyRef = useRef(false);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);

  const switchMode = (next: AuthMode) => {
    if (busyRef.current) return;
    setMode(next); setError(''); setMessage(''); setLastFailedAction(null); setConfirmationEmail('');
    setPassword(''); setConfirmPassword('');
  };

  const runAction = async (action: AuthAction, operation: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusyAction(action); setError(''); setLastFailedAction(null);
    try { assertOnline(); await operation(); }
    catch (reason) {
      void reportClientError({ code: `auth_${action}`, message: reason instanceof Error ? reason.message : String(reason), severity: 'warning' });
      setLastFailedAction(action);
      setError(friendlyAuthError(reason instanceof Error ? reason.message : 'ดำเนินการไม่สำเร็จ'));
    } finally { busyRef.current = false; setBusyAction(null); }
  };

  const performSubmit = async () => {
    if (!supabase || busyRef.current) return;
    const client = supabase;
    if ((mode === 'signup' || mode === 'recovery') && password !== confirmPassword) { setError('รหัสผ่านทั้งสองช่องไม่ตรงกัน'); return; }
    if (mode === 'signup' && !acceptedTerms) { setError('กรุณายอมรับเงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัว'); return; }
    await runAction(mode, async () => {
      setMessage('');
      if (mode === 'signin') {
        const { error: signInError } = await withTimeout(client.auth.signInWithPassword({ email: email.trim(), password }));
        if (signInError) throw signInError;
      } else if (mode === 'signup') {
        const { data, error: signUpError } = await withTimeout(client.auth.signUp({
          email: email.trim(), password,
          options: { emailRedirectTo: window.location.origin, data: { full_name: name.trim(), terms_accepted_at: new Date().toISOString() } },
        }));
        if (signUpError) throw signUpError;
        if (!data.session) {
          setConfirmationEmail(email.trim());
          setMessage(`สร้างบัญชีแล้ว ระบบส่งอีเมลยืนยันไปที่ ${email.trim()} กรุณาตรวจกล่องขาเข้าและสแปม`);
        }
      } else if (mode === 'forgot') {
        const { error: resetError } = await withTimeout(client.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/?recovery=1` }));
        if (resetError) throw resetError;
        setMessage(`รับคำขอแล้ว หาก ${email.trim()} มีบัญชี BilaFlow ระบบจะส่งลิงก์ตั้งรหัสผ่านให้`);
      } else {
        const { error: updateError } = await withTimeout(client.auth.updateUser({ password }));
        if (updateError) throw updateError;
        await withTimeout(client.auth.signOut(), 10_000).catch(() => undefined);
        window.history.replaceState({}, '', window.location.pathname);
        onRecoveryComplete(); switchMode('signin');
        setMessage('ตั้งรหัสผ่านใหม่เรียบร้อยแล้ว คุณเข้าสู่ระบบได้ทันที');
      }
    });
  };

  const submit = (event: FormEvent) => { event.preventDefault(); void performSubmit(); };

  const signInWithGoogle = async () => {
    if (!supabase) return;
    const client = supabase;
    await runAction('google', async () => {
      setMessage('');
      const { error: oauthError } = await withTimeout(client.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } }));
      if (oauthError) throw oauthError;
    });
  };

  const resendConfirmation = async () => {
    if (!supabase || !confirmationEmail) return;
    const client = supabase;
    await runAction('resend', async () => {
      const { error: resendError } = await withTimeout(client.auth.resend({ type: 'signup', email: confirmationEmail, options: { emailRedirectTo: window.location.origin } }));
      if (resendError) throw resendError;
      setMessage(`ส่งอีเมลยืนยันไปที่ ${confirmationEmail} อีกครั้งแล้ว`);
    });
  };

  const retryLastAction = () => {
    if (lastFailedAction === 'google') void signInWithGoogle();
    else if (lastFailedAction === 'resend') void resendConfirmation();
    else void performSubmit();
  };

  if (!configured) return <main className="auth-shell setup-required">
    <section className="auth-brand-panel"><div className="auth-brand"><span>BF</span><strong>BilaFlow</strong></div><div><span className="auth-kicker">Quotation to receipt</span><h1>เอกสารธุรกิจ<br />ที่ข้อมูลเป็นของคุณ</h1><p>สร้างใบเสนอราคา ออกใบแจ้งหนี้ และติดตามการรับชำระในพื้นที่เดียว</p></div><div className="auth-trust"><ShieldCheck size={19} /><span>ออกแบบโดยให้ความเป็นส่วนตัวมาก่อน</span></div></section>
    <section className="auth-card unavailable-card"><div className="auth-card-heading"><LockKeyhole size={25} /><div><span className="eyebrow">Temporarily unavailable</span><h2>ระบบยังไม่พร้อมใช้งาน</h2><p>ขณะนี้เรากำลังเตรียมระบบเพื่อให้บริการ กรุณาลองใหม่อีกครั้งภายหลัง</p></div></div><button className="btn primary auth-submit" type="button" onClick={() => window.location.reload()}>ลองใหม่อีกครั้ง</button><p className="auth-footnote">หากคุณเคยใช้งาน ข้อมูลที่บันทึกไว้ในอุปกรณ์นี้จะยังไม่ถูกลบ</p></section>
  </main>;

  const heading = mode === 'signup' ? 'สร้างบัญชี Free' : mode === 'forgot' ? 'ลืมรหัสผ่าน' : mode === 'recovery' ? 'ตั้งรหัสผ่านใหม่' : 'ยินดีต้อนรับกลับมา';
  const description = mode === 'signup' ? 'สมัครครั้งเดียว แล้วเริ่มสร้างเอกสารได้ทันที' : mode === 'forgot' ? 'เราจะส่งลิงก์ตั้งรหัสผ่านใหม่ไปยังอีเมลของคุณ' : mode === 'recovery' ? 'ตั้งรหัสผ่านใหม่ที่ปลอดภัยสำหรับบัญชีของคุณ' : 'เข้าสู่ระบบเพื่อเปิดพื้นที่เอกสารของคุณ';
  const busy = busyAction !== null;

  return <main className="auth-shell">
    <section className="auth-brand-panel">
      <div className="auth-brand"><span>BF</span><strong>BilaFlow</strong></div>
      <div><span className="auth-kicker">Quotation to receipt</span><h1>จัดการเอกสารธุรกิจ<br />อย่างเป็นส่วนตัว</h1><p>สร้างใบเสนอราคา ติดตามการยอมรับ ออกใบแจ้งหนี้ และรับชำระครบในที่เดียว</p></div>
      <ul className="auth-benefits"><li><CheckCircle2 /> เริ่มต้นฟรี</li><li><CheckCircle2 /> ข้อมูล Free อยู่ในอุปกรณ์</li><li><CheckCircle2 /> สำรองและย้ายข้อมูลได้</li></ul>
      <div className="auth-trust"><ShieldCheck size={19} /><span>Admin ไม่สามารถอ่านเอกสารของคุณ</span></div>
    </section>
    <section className="auth-card">
      <div className="auth-card-heading"><div><span className="eyebrow">บัญชี BilaFlow</span><h2>{heading}</h2><p>{description}</p></div></div>
      {!online && <div className="auth-offline" role="status"><WifiOff size={17} /><span>อินเทอร์เน็ตขาดการเชื่อมต่อ กรอกข้อมูลรอไว้ได้ แล้วลองใหม่เมื่อเน็ตกลับมา</span></div>}
      {mode !== 'forgot' && mode !== 'recovery' && <button className="google-button" type="button" onClick={() => void signInWithGoogle()} disabled={busy}>{busyAction === 'google' ? <><RefreshCw className="spin" size={16} /> {actionLabels.google}</> : <><span>G</span> ดำเนินการต่อด้วย Google</>}</button>}
      {mode !== 'forgot' && mode !== 'recovery' && <div className="auth-divider"><span>หรือใช้อีเมล</span></div>}
      <form className="auth-main-form" onSubmit={submit} aria-busy={busy}>
        {mode === 'signup' && <label><span>ชื่อผู้ใช้งาน</span><input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required /></label>}
        {mode !== 'recovery' && <label><span>อีเมล</span><div className="auth-input"><Mail size={17} /><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></div></label>}
        {mode !== 'forgot' && <label><span>{mode === 'recovery' ? 'รหัสผ่านใหม่' : 'รหัสผ่าน'}</span><div className="auth-input"><KeyRound size={17} /><input type={showPassword ? 'text' : 'password'} minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} required /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}>{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button></div></label>}
        {(mode === 'signup' || mode === 'recovery') && <label><span>ยืนยันรหัสผ่าน</span><div className="auth-input"><KeyRound size={17} /><input type={showPassword ? 'text' : 'password'} minLength={8} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" required /></div></label>}
        {mode === 'signup' && <label className="auth-consent"><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} /><span>ฉันยอมรับ <a href="/terms.html" target="_blank" rel="noreferrer">เงื่อนไขการใช้งาน</a> และ <a href="/privacy.html" target="_blank" rel="noreferrer">นโยบายความเป็นส่วนตัว</a></span></label>}
        {error && <div className="auth-error-row" role="alert"><p className="auth-error">{error}</p>{lastFailedAction && <button type="button" className="auth-retry" onClick={retryLastAction} disabled={busy || !online}><RefreshCw size={14} /> ลองใหม่</button>}</div>}
        {message && <div className="auth-success" role="status"><CheckCircle2 size={16} /><span>{message}</span></div>}
        {confirmationEmail && <button className="auth-resend" type="button" onClick={() => void resendConfirmation()} disabled={busy}>{busyAction === 'resend' ? actionLabels.resend : 'ยังไม่ได้รับ? ส่งอีเมลยืนยันอีกครั้ง'}</button>}
        <button className="btn primary auth-submit" disabled={busy}>{busyAction && busyAction !== 'google' && busyAction !== 'resend' ? actionLabels[busyAction] : mode === 'signup' ? 'สร้างบัญชี Free' : mode === 'forgot' ? 'ส่งลิงก์ตั้งรหัสผ่าน' : mode === 'recovery' ? 'บันทึกรหัสผ่านใหม่' : 'เข้าสู่ระบบ'} <ArrowRight size={17} /></button>
      </form>
      <div className="auth-links">
        {mode === 'signin' && <><button onClick={() => switchMode('forgot')} disabled={busy}>ลืมรหัสผ่าน?</button><span>ยังไม่มีบัญชี? <button onClick={() => switchMode('signup')} disabled={busy}>สมัครสมาชิก</button></span></>}
        {mode === 'signup' && <span>มีบัญชีแล้ว? <button onClick={() => switchMode('signin')} disabled={busy}>เข้าสู่ระบบ</button></span>}
        {(mode === 'forgot' || mode === 'recovery') && <button onClick={() => switchMode('signin')} disabled={busy}>กลับไปหน้าเข้าสู่ระบบ</button>}
      </div>
      {onBack && !recoveryMode && <button className="auth-back-home" type="button" onClick={onBack} disabled={busy}>← กลับไปดูหน้าแนะนำและตัวอย่างระบบ</button>}
      <p className="auth-footnote">แพ็กเกจ Free เก็บเอกสารในอุปกรณ์นี้ การสมัครสมาชิกไม่ได้เปิด Cloud Sync อัตโนมัติ</p>
    </section>
  </main>;
}
